import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Package, Layers, GitBranch, Thermometer, FileText, Users, CheckCircle2, Play } from 'lucide-react';
import { builderResources, partnerDocs } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import SEO, { breadcrumbSchema } from '../components/SEO';

const ICON_MAP = { Package, Layers, GitBranch, Thermometer, FileText, Users };

const BuilderResources = () => {
  const { tx, language } = useLanguage();
  const fr = language === 'fr';
  const [videoPlaying, setVideoPlaying] = useState(false);
  return (
    <div className="min-h-screen pt-[76px]" style={{ backgroundColor: '#000' }}>
      <SEO
        title="Builder Resources"
        description="Technical resources for builders working with Total Composites shells — assembly guides, installation videos, material specs, and documentation for expedition camper construction."
        path="/builder-resources"
        keywords="truck camper assembly guide, composite panel construction, Total Composites builder docs, camper build instructions, FRP assembly tutorial"
        jsonLd={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Builder Resources', path: '/builder-resources' }
        ])}
      />
      {/* Header */}
      <section className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#E8520A', letterSpacing: '0.15em' }}>
            {fr ? 'Pour les constructeurs' : 'For Builders'}
          </p>
          <h1 className="text-4xl lg:text-5xl font-black text-white mb-4 uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>
            {fr ? 'Ressources constructeurs' : 'Builder Resources'}
          </h1>
          <p className="text-gray-400 max-w-xl leading-relaxed">
            {fr
              ? "Informations techniques, guides d’assemblage et ressources d’ingénierie pour les constructeurs professionnels et les partenaires."
              : 'Technical information, assembly guides, and engineering resources for professional builders and partners.'}
          </p>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {builderResources.map((resource, i) => {
              const IconComp = ICON_MAP[resource.icon];
              return (
                <div key={resource.title?.en || resource.icon} className="border border-gray-100 rounded-xl p-7 hover:border-orange-200 hover:shadow-lg transition-all duration-300 group">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                    style={{ backgroundColor: '#FFF0E8' }}
                  >
                    {IconComp && <IconComp size={22} style={{ color: '#E8520A' }} />}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors">
                    {tx(resource.title)}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-5">{tx(resource.description)}</p>
                  <ul className="space-y-2">
                    {resource.points.map((point, j) => (
                      <li key={`${resource.title}-pt-${j}`} className="flex items-start gap-2.5 text-xs text-gray-600">
                        <CheckCircle2 size={13} className="flex-shrink-0 mt-0.5" style={{ color: '#E8520A' }} />
                        {tx(point)}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Video Resource Example */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#E8520A', letterSpacing: '0.15em' }}>
                {fr ? 'Exemple de ressource' : 'Resource Example'}
              </p>
              <h2 className="text-3xl lg:text-4xl font-black text-white mb-5 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
                {fr ? <>Guides d'assemblage<br />et formation vidéo</> : <>Assembly Guides<br />& Video Training</>}
              </h2>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                {fr
                  ? "Nos partenaires et constructeurs ont accès à des guides vidéo détaillés couvrant les techniques d'assemblage, les spécifications des matériaux et les meilleures pratiques de construction. Voici un exemple du type de contenu technique que nous fournissons."
                  : "Our partners and builders get access to detailed video guides covering assembly techniques, material specifications, and construction best practices. Here's an example of the kind of technical content we provide."}
              </p>
              <div className="flex items-center gap-3 text-gray-500 text-xs">
                <Play size={14} style={{ color: '#E8520A' }} />
                <span>{fr ? 'Vidéo technique — Assemblage de panneaux composites' : 'Technical Video — Composite Panel Assembly'}</span>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl relative group cursor-pointer" style={{ aspectRatio: '16/9' }} data-testid="builder-resource-video">
              {videoPlaying ? (
                <iframe
                  src="https://www.youtube.com/embed/8qQZ_8Lo87M?autoplay=1"
                  title="Total Composites — Builder Resource Video"
                  className="w-full h-full absolute inset-0"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div className="w-full h-full relative" onClick={() => setVideoPlaying(true)}>
                  <img
                    src="https://customer-assets.emergentagent.com/job_2b9ac9a3-5021-4c38-a587-e6fe0b7ed1ab/artifacts/l14l7l3x_image.png"
                    alt="Total Composites FRP Pultrusion / Adhesive Assembly"
                    className="w-full h-full object-cover"
                  />
                  {/* Dark overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-30 group-hover:bg-opacity-20 transition-all duration-300" />
                  {/* YouTube-style play button */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div
                      className="flex items-center justify-center transition-all duration-200 group-hover:scale-110"
                      style={{
                        width: 68,
                        height: 48,
                        backgroundColor: 'rgba(255,0,0,0.85)',
                        borderRadius: 12,
                      }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                  {/* Video title bar */}
                  <div className="absolute bottom-0 left-0 right-0 px-4 py-3" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.8))' }}>
                    <p className="text-white text-sm font-semibold truncate">{fr ? 'Total Composites — Pultrusion FRP / Assemblage adhésif' : 'Total Composites FRP Pultrusion/Adhesive Assembly'}</p>
                    <p className="text-gray-400 text-xs mt-0.5">Total Composites</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Specs for Partners */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-start">
            <div>
              <h2 className="text-3xl font-black text-gray-900 mb-5" style={{ fontFamily: 'Inter, sans-serif' }}>
                {fr ? 'Spécifications détaillées pour les partenaires' : 'Detailed Specs for Partners'}
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4 text-sm">
                {fr
                  ? "Nous fournissons toutes les données et spécifications disponibles à nos partenaires commerciaux. Les solutions de matériaux et de construction détaillées sont considérées comme confidentielles et partagées sous accord de confidentialité."
                  : 'We provide all available data and specifications to our commercial partners. Detailed material and construction solutions are considered confidential and shared under NDA.'}
              </p>
              <p className="text-gray-600 leading-relaxed mb-8 text-sm">
                {fr
                  ? "Si vous êtes un partenaire existant ayant besoin de documentation technique, ou un partenaire potentiel intéressé par nos spécifications détaillées, veuillez nous contacter."
                  : "If you're an existing partner needing technical documentation, or a prospective partner interested in our detailed specifications, please get in touch."}
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 font-bold text-white rounded-full text-sm transition-all duration-200 hover:opacity-90 hover:scale-105"
                style={{ backgroundColor: '#E8520A' }}
              >
                {fr ? 'DEMANDER LES DONNÉES TECHNIQUES' : 'REQUEST TECHNICAL DATA'} <ArrowRight size={16} />
              </Link>
            </div>
            <div>
              <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-5">
                {fr ? 'Ce que les partenaires reçoivent' : 'What Partners Receive'}
              </h3>
              <div className="space-y-3">
                {partnerDocs.map((doc, i) => (
                  <div key={doc.title || doc.url} className="flex items-center gap-3 bg-white rounded-lg px-5 py-3.5 border border-gray-100 shadow-sm">
                    <CheckCircle2 size={15} style={{ color: '#E8520A' }} className="flex-shrink-0" />
                    <span className="text-sm text-gray-700">{tx(doc)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BuilderResources;
